import h from 'hyperscript';

import type { MenuContentElement } from 'ui/menu/menu';
import { MENU_ITEM_SEPARATOR } from 'ui/menu/menu';
import { showToastMessage } from 'ui/toast-message/toast-message';

import type { EnrichmentType } from 'cadenza/workbook/api/enrichment-type';
import { confirm } from 'cadenza/alert';
import { setEnrichmentGrouping } from 'cadenza/api-client/objecttypes-api';
import { releaseWorkbookObjecttype } from 'cadenza/api-client/workbook-api';
import { Command } from 'cadenza/api-client/workbook-commands';
import { type ObjecttypeIdString } from 'cadenza/repository/model/objecttype-id';
import { getStore } from 'cadenza/store/cadenza-store';
import { logger } from 'cadenza/utils/logging';
import type { Objecttype } from 'cadenza/workbook/api/objecttype';
import { isObjecttypeBound, isObjecttypePermanentAndBound } from 'cadenza/workbook/api/objecttype';
import type { EnrichmentDto } from 'cadenza/workbook/enrichment/model/enrichment-dto';
import { getEnrichmentDetailsContextActionsRulesCheckedResult } from 'cadenza/workbook/workbook-panel/databrowser/enrichment-details-context-actions-rule-checker';
import { EnrichmentSummary } from 'cadenza/workbook/workbook-panel/databrowser/enrichment-summary/enrichment-summary';
import {
  executeCommand,
  getObjecttypeById,
  isObjecttypeUsed,
  removeObjectTypes,
  updateObjecttypeBoundState,
} from 'cadenza/workbook/workbook.state';
import { executeRerun } from 'cadenza/workbook/enrichment/rerun/rerun-enrichment';
import type { OutdatedEnrichmentsContext } from 'cadenza/workbook/workbook-panel/databrowser/outdated-enrichments-context';

import i18n from './data-browser-enrichment-context-actions.properties';

export async function createEnrichmentActions({
  enrichment,
  isMissingEnrichedObjecttype = false,
  outdatedEnrichmentsContext,
}: {
  enrichment: EnrichmentDto;
  isMissingEnrichedObjecttype?: boolean;
  outdatedEnrichmentsContext?: OutdatedEnrichmentsContext;
}) {
  const enrichmentObjecttype = getObjecttypeById(getStore().getState(), enrichment.objecttypeId);
  const enrichmentActions: MenuContentElement[] = [
    new EnrichmentSummary(createEnrichmentSummaryEnrichment(enrichment), outdatedEnrichmentsContext),
  ];
  const {
    isUserAllowedToChangeGroupingOrUngrouping,
    isUserAllowedToRerun,
    isUserAllowedToDelete,
    isUserAllowedToRelease,
  } = await getEnrichmentDetailsContextActionsRulesCheckedResult(enrichment, isMissingEnrichedObjecttype);
  if (
    isUserAllowedToChangeGroupingOrUngrouping ||
    isUserAllowedToRerun ||
    isUserAllowedToDelete ||
    isUserAllowedToRelease
  ) {
    enrichmentActions.push(MENU_ITEM_SEPARATOR);
  }
  if (isUserAllowedToChangeGroupingOrUngrouping && !isMissingEnrichedObjecttype) {
    enrichmentActions.push(createToggleGroupEnrichmentAction(enrichment));
  }
  if (isUserAllowedToRerun) {
    enrichmentActions.push(createRerunEnrichmentAction(enrichment));
  }
  if (isUserAllowedToDelete) {
    enrichmentActions.push(createDeleteEnrichmentAction(enrichment));
  }
  if (isUserAllowedToRelease && isObjecttypePermanentAndBound(enrichmentObjecttype)) {
    const enrichedObjecttype = getObjecttypeById(getStore().getState(), enrichment.enrichedObjecttypeId);
    if (isMissingEnrichedObjecttype || !isObjecttypePermanentAndBound(enrichedObjecttype)) {
      enrichmentActions.push(createReleaseEnrichmentAction(enrichmentObjecttype));
    }
  }
  return enrichmentActions;
}

function createToggleGroupEnrichmentAction(enrichment: EnrichmentDto) {
  return {
    label: enrichment.grouped ? i18n('actions.ungroupEnrichment') : i18n('actions.groupEnrichment'),
    execute: async () => {
      await setEnrichmentGrouping(enrichment.objecttypeId, !enrichment.grouped);
      // trigger data browser reload
      getStore().dispatch(
        executeCommand(
          Command.ADD_OBJECTTYPES({
            objecttypeIds: [enrichment.objecttypeId],
          }),
        ),
      );
    },
  };
}

function createRerunEnrichmentAction(enrichmentDto: EnrichmentDto) {
  return {
    label: i18n('actions.recalculateEnrichment'),
    execute: async () => executeRerun(enrichmentDto),
  };
}

function createDeleteEnrichmentAction(enrichment: EnrichmentDto) {
  const enrichmentObjectType = getObjecttypeById(getStore().getState(), enrichment.objecttypeId);
  return {
    label: i18n('actions.deleteEnrichment'),
    execute: () => removeObjecttypeFromWorkbook(enrichmentObjectType),
  };
}

function removeObjecttypeFromWorkbook(objecttype: Objecttype) {
  const usedInWorkbook = isObjecttypeUsed(getStore().getState(), objecttype.id);
  const boundToWorkbook = isObjecttypeBound(objecttype);
  if (!usedInWorkbook && !boundToWorkbook) {
    removeEnrichmentAndCleanup(objecttype.id);
  } else if (usedInWorkbook && !boundToWorkbook) {
    confirmAndRemoveEnrichment(
      objecttype.id,
      i18n('remove.enrichment.confirm.used.title'),
      i18n('remove.enrichment.confirm.used.message'),
      i18n('remove.button.deleteAnyway'),
    );
  } else if (!usedInWorkbook && boundToWorkbook) {
    confirmAndRemoveEnrichment(
      objecttype.id,
      i18n('remove.enrichment.confirm.bound.title'),
      i18n('remove.enrichment.confirm.bound.message'),
      i18n('remove.button.deleteAnyway'),
    );
  } else if (usedInWorkbook && boundToWorkbook) {
    confirmAndRemoveEnrichment(
      objecttype.id,
      i18n('remove.enrichment.confirm.usedAndBound.title'),
      i18n('remove.enrichment.confirm.usedAndBound.message'),
      i18n('remove.button.deleteAnyway'),
    );
  }
}

function removeEnrichmentAndCleanup(enrichmentId: ObjecttypeIdString) {
  const objecttypeIds = [enrichmentId];
  getStore().dispatch(executeCommand(Command.REMOVE_OBJECTTYPES({ objecttypeIds })));
  getStore().dispatch(removeObjectTypes(objecttypeIds));
  return Promise.resolve();
}

function confirmAndRemoveEnrichment(
  objecttypeId: ObjecttypeIdString,
  confirmTitle: string,
  confirmMessage: string,
  confirmApplyMessage: string,
) {
  confirm(confirmTitle, confirmMessage, {
    type: 'warning',
    submit: i18n('common:cancel'),
    alternativeSubmit: confirmApplyMessage,
    cancel: false,
    alternativeSubmitCallback: () => removeEnrichmentAndCleanup(objecttypeId),
  });
}

function releaseEnrichment(objecttypeId: ObjecttypeIdString) {
  return releaseWorkbookObjecttype(objecttypeId)
    .then(() => {
      getStore().dispatch(
        updateObjecttypeBoundState({
          objecttypeId,
          boundState: 'UNBOUND',
        }),
      );
      showToastMessage(i18n('actions.releaseEnrichment.success'), { type: 'success' });
    })
    .catch((error) => {
      logger.error(error);
      showToastMessage(i18n('actions.releaseEnrichment.failure'), { type: 'error' });
    });
}

function createReleaseEnrichmentAction(enrichmentObjecttype: Objecttype) {
  const objecttypeId = enrichmentObjecttype.id;
  return {
    label: i18n('actions.releaseEnrichment.label'),
    execute: () => {
      confirm(
        i18n('actions.releaseEnrichment.confirm.title'),
        h('div', [
          h('div', i18n('actions.releaseEnrichment.confirm.message.top')),
          h('div', i18n('actions.releaseEnrichment.confirm.message.bottom'), {
            style: { 'padding-top': '8px' },
          }),
        ]),
        {
          type: 'info',
          cancel: i18n('common:cancel'),
          submit: i18n('actions.releaseEnrichment.confirm.continue'),
          submitCallback: () => releaseEnrichment(objecttypeId),
        },
      );
    },
  };
}

function createEnrichmentSummaryEnrichment(enrichment: EnrichmentDto) {
  const enrichmentObjecttype = getObjecttypeById(getStore().getState(), enrichment.objecttypeId);
  const objecttypeProperties = enrichmentObjecttype.properties ?? {};
  return {
    repositoryName: enrichment.repositoryName,
    enrichedObjecttypeId: enrichment.enrichedObjecttypeId,
    enrichmentObjecttypeId: enrichmentObjecttype.id,
    printName: enrichmentObjecttype.printName,
    grouped: enrichment.grouped,
    // we know that this is enrichment type because this is enrichment action
    enrichmentType: objecttypeProperties['self-service-content-role'] as EnrichmentType,
  };
}
